'use client';

import { faFilter } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { SeoProfessionalsResult } from '@medprev-web-app/api-graphql-client';

import { ProfessionalsCardByLetter } from './professionals-card-by-letter.component';
import { ProfessionalModalByLetter } from './professional-modal-by-letter.component';

import { ContentContainer } from '@/components/ui/content-container.component';
import { EntityByLetterCount } from '@/types/professional/professional-by-letters.types';
import { Pill } from '@/components/ui/pill.component';
import { Icon } from '@/components/ui/icon.component';
import { useModal } from '@/hooks/use-modal.hook';
import { Button } from '@/components/ui/button/button.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { AlphabetLetterEnum } from '@/types/enums/alphabet.enum';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

interface ProfessionalListProps {
  letter?: AlphabetLetterEnum;
  professionals: SeoProfessionalsResult[];
  count: EntityByLetterCount;
  shouldShowLetter?: boolean;
}

export function ProfessionalList({
  letter,
  professionals,
  count,
  shouldShowLetter = true,
}: ProfessionalListProps) {
  const { open, onOpenChange } = useModal();
  const letterUpper = letter?.toUpperCase();
  const professionalsByLetter = professionals;

  return (
    <>
      <ContentContainer className="mt-6 md:mb-10 px-0 sm:px-0">
        <div className="px-6 pb-6 md:pb-0 sm:px-16 md:px-0">
          <Typography
            variant={TypographyVariant.TITLE}
            className="text-center mb-3 lg:text-left"
            data-testid={
              letterUpper
                ? 'title-selected--professional-title'
                : 'title--professional-title'
            }
          >
            {letter ? `Profissionais com ${letterUpper}` : 'Profissionais'}
          </Typography>

          <div className="mt-4 sm:mt-5">
            <Pill
              variant={letter ? 'success' : 'light'}
              icon={faFilter}
              asChild
              data-test-id="filter-letter--professional-list"
            >
              <Button
                data-testid={
                  DataTestIdEnum.PROFESSIONALS_INDEX_LETTER_FILTER_BUTTON
                }
                className="flex items-center gap-1 hover:no-underline"
                variant="text"
                data-analytics-event={AnalyticsEnum.CLICK_PROFESSIONAL_INDEX_FILTER_BY_LETTER}
                onClick={() => {
                  onOpenChange(true);
                }}
              >
                {letter
                  ? (
                      <>
                        <Icon icon={faFilter} className="text-success-800" />
                        <span className="text-success-700 font-semibold">
                          Letra
                          {' '}
                          {letterUpper}
                        </span>
                      </>
                    )
                  : (
                      <>
                        <Icon icon={faFilter} className="text-gray-800" />
                        <span className="text-gray-700 font-semibold">
                          Filtrar por letra
                        </span>
                      </>
                    )}
              </Button>
            </Pill>
          </div>
        </div>

        {professionalsByLetter.map((list) => {
          if (list.professionals.length === 0) {
            return null;
          }

          return (
            <ProfessionalsCardByLetter
              key={list.letter}
              letter={list.letter}
              hasFilter={!!letterUpper}
              professionals={list.professionals}
              shouldShowLetter={shouldShowLetter}
            />
          );
        })}
      </ContentContainer>

      <ProfessionalModalByLetter
        open={open}
        onOpenChange={onOpenChange}
        selectedLetter={letterUpper}
        count={count}
      />
    </>
  );
}
